/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, April 13th 2022, 5:50:22 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useToggleState } from "ui-widgets/dialogs/useToggleState";
import { OSAPIForm, TextFormInput } from "ui-widgets/forms";
import { Tool } from "react-feather";
import { COLORSCHEME } from "theme/styles";
import { SinglePortalSelectInput } from "portals/components/PortalSelectorInputs";
import { TeamsTransferListInput } from "teams/components/TeamSelectorInputs";
import { useGlobalScope } from "core/providers";
import { PreBuiltService, InstallPreBuiltService } from "lib/schemas";
import { StyledOSDialog } from "ui-widgets";
import { convertTextToInitials } from "lib/utils";
import { useInstallPreBuiltServiceAPIForm } from "prebuilt-services/services/PreBuiltServicesAPI";
import { InstallationProgressComponent } from "./InstallationProgressComponent";

export interface InstallationFormProps {
  serviceTitle: string;
  isAlreadyInstalled: boolean;
  installationFormOpenState: boolean;
  closeInstallationFormFn: () => void;
  prebuiltServiceId?: PreBuiltService["id"];
  serviceCode?: string;
  installationFile?: File;
}
export const InstallationForm = (props: InstallationFormProps) => {
  const {
    useAPIFormReturn,
    defaultServiceCode,
    installationProgressOpenState,
    closeInstallationProgress,
    installPreBuiltServiceResponse,
  } = useInstallationFormLogic(props);

  return (
    <>
      <StyledOSDialog
        popUpIcon={<Tool color={COLORSCHEME.secondary} />}
        popUpTitle="Complete Installation"
        open={props.installationFormOpenState}
        onClose={props.closeInstallationFormFn}
      >
        <OSAPIForm
          useAPIFormReturn={useAPIFormReturn}
          submitButtonText={props.isAlreadyInstalled ? "Update" : "Install"}
        >
          <TextFormInput<InstallPreBuiltService>
            schema_field_key="service_code"
            label="Service Code"
            defaultValue={defaultServiceCode}
          />
          <SinglePortalSelectInput<InstallPreBuiltService>
            schema_field_key="site_id"
            label="Choose a Portal for the service"
          />
          <TeamsTransferListInput<InstallPreBuiltService>
            schema_field_key="team_ids"
            label="Choose Teams"
            helperText="Choose Teams you want to access the installed service. You can also do this later."
          />
        </OSAPIForm>
      </StyledOSDialog>
      <InstallationProgressComponent
        serviceTitle={props.serviceTitle}
        isAlreadyInstalled={props.isAlreadyInstalled}
        installationProgressOpenState={installationProgressOpenState}
        closeInstallationProgress={closeInstallationProgress}
        isInstalling={useAPIFormReturn.isSendingDataToAPI}
        hasInstallationError={useAPIFormReturn.isAPIDataSendError}
        isSuccessfulInstallation={useAPIFormReturn.isSuccessfulAPIDataSend}
        installationError={`${(useAPIFormReturn.apiDataSendErrors as any)?.detail}`}
        installed_process_definition_id={installPreBuiltServiceResponse?.installed_process_definition_id}
      />
    </>
  );
};

const useInstallationFormLogic = ({
  serviceCode,
  prebuiltServiceId,
  closeInstallationFormFn,
  installationFile,
}: InstallationFormProps) => {
  const { selectedOrganization } = useGlobalScope().globalState;

  const defaultServiceCode =
    serviceCode && `${serviceCode}-${convertTextToInitials(selectedOrganization.name).join("")}`;

  const { useAPIFormReturn, installPreBuiltServiceResponse } = useInstallPreBuiltServiceAPIForm({
    organizationId: selectedOrganization.id,
    defaultServiceCode,
    prebuiltServiceId,
    installationFile,
  });

  const {
    handleClose: closeInstallationProgress,
    open: installationProgressOpenState,
    handleOpen: openInstallationProgress,
  } = useToggleState();

  React.useEffect(() => {
    if (useAPIFormReturn.isSendingDataToAPI) {
      openInstallationProgress();
      closeInstallationFormFn();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useAPIFormReturn.isSendingDataToAPI]);

  return {
    useAPIFormReturn,
    defaultServiceCode,
    installationProgressOpenState,
    closeInstallationProgress,
    installPreBuiltServiceResponse,
  };
};
